<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div class="k-asset-review-comment" :class="{'k-resolved': resolved, 'k-asset-review-comment-reply': !top_level}">
		<div v-if="!editing">
			<div class="k-asset-review-comment-header d-flex align-center" :class="{'k-resolved': resolved}">
				<div class="d-flex">
					Comment{{top_level ? '' : ' Reply'}} by {{local_comment.author_name}} 
					(
						<k-tooltip bottom>
							<template v-slot:activator="{on}">
								<a v-on="on" :href="`mailto:${local_comment.author_email}`">{{local_comment.author_email}}</a>
							</template>
							Email {{ local_comment.author_name }}
						</k-tooltip>

						<k-tooltip bottom>
							<template v-slot:activator="{on}">
								<k-icon v-on="on" class="ml-2" x-small @click="copy_email_to_clipboard">fas fa-copy</k-icon>
							</template>
							Copy email address to clipboard
						</k-tooltip>
					)
				</div>

				<k-tooltip v-if="resolved" bottom>
					<template v-slot:activator="{on}">
						<k-icon v-on="on" color="#555" class="ml-2" style="margin-top: 2px" small>fas fa-check-double</k-icon>
					</template>
					Comment Resolved
				</k-tooltip>

				<k-spacer/>

				<div v-html="comment_date"></div>

				<k-tooltip v-if="top_level && resolved" bottom>
					<template v-slot:activator="{on}">
						<k-btn v-on="on" class="ml-2" :aria-label="collapsed ? 'Expand' : 'Collapse'" x-small icon @click="collapsed = !collapsed">
							<k-icon>fas {{ collapsed ? 'fa-angle-down' : 'fa-angle-left' }}</k-icon>
						</k-btn>
					</template>
					{{ collapsed ? 'Expand' : 'Collapse'}}
				</k-tooltip>
			</div>

			<div class="k-asset-review-comment-body" :class="{'k-collapsed': collapsed}">
				<div class="d-flex">
					<div class="fr-view" v-html="U.render_latex(local_comment.text)"></div>
					
					<k-spacer/>

					<k-tooltip v-if="can_toggle_resolved && top_level" bottom>
						<template v-slot:activator="{on}">
							<k-btn v-on="on" color="#666" class="k-tight-btn" small icon :aria-label="'Mark ' + (resolved ? 'Unresolved' : 'Resolved')" @click="toggle_resolved">
								<k-icon small>fas {{ resolved ? 'fa-circle-minus' : 'fa-check-double'}}</k-icon>
							</k-btn>
						</template>
						Mark {{ resolved ? 'Unresolved' : 'Resolved'}}
					</k-tooltip>

					<k-menu v-if="can_edit" bottom left>
						<template v-slot:activator="{on}">
							<k-btn v-on="on" small icon aria-label="Comment options">
								<k-icon small>fas fa-ellipsis-v</k-icon>
							</k-btn>
						</template>

						<k-list>
							<k-list-item @click="edit_comment">
								<k-list-item-icon>
									<k-icon small>fas fa-edit</k-icon>
								</k-list-item-icon>
								<k-list-item-title>Edit</k-list-item-title>
							</k-list-item>

							<k-list-item @click="delete_comment(false)">
								<k-list-item-icon>
									<k-icon small>fas fa-trash</k-icon>
								</k-list-item-icon>
								<k-list-item-title>Delete</k-list-item-title>
							</k-list-item>
						</k-list>
					</k-menu>
				</div>

				<!-- replies -->
				<AssetReviewComment
					v-for="(c) in replies"
					:key="c.id"
					class="mb-2"
					:comment="c"
					:template="template"
					:review_section="review_section"
					:parent_resolved="resolved"
					:top_level="false"
					@save_comment="save_comment($event, false)"
					@delete_comment="delete_comment(c)"
				/>

				<div class="d-flex mt-1">
					<k-spacer/>
					
					<!-- only allow replies on top-level comments -->
					<k-btn
						v-if="!new_comment && top_level && !resolved"
						color="#444"
						x-small 
						text
						dark
						@click="generate_new_comment"
					>
						New Comment Reply<k-icon small class="ml-2">fas fa-comment</k-icon>
					</k-btn>
				</div>

				<div class="ml-8" v-if="new_comment">
					<AssetReviewCommentEditor
						:comment="new_comment"
						@save_comment="save_comment($event)"
						@cancel_comment="new_comment=null"
					/>
				</div>
			</div>
		</div>

		<div v-else>
			<AssetReviewCommentEditor
				:comment="local_comment"
				editing
				@save_comment="save_comment($event)"
				@cancel_comment="editing = false"
			/>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import AssetReviewComment from './AssetReviewComment.vue'
import AssetReviewCommentEditor from './AssetReviewCommentEditor.vue'

export default {
	name: 'AssetReviewComment',

	components: {AssetReviewComment, AssetReviewCommentEditor},

	props: {
		template: { required: true },
		review_section: { type: Object, required: true },
		comment: { type: Object, required: true },
		parent_resolved: {type: Boolean, default: false},
		top_level: {type: Boolean, default: true},
	},

	data() { return {
		new_comment: null,
		editing: false,
		local_comment: {},
		collapsed: false,
	}},

	computed: {
		...mapState(['site_config', 'user_info']),

		comment_date() {
			return date.format(new Date(this.local_comment.created_at * 1000), 'MMM D, YYYY h:mm A')
		},

		replies() {
			return this.review_section.comments.filter(c => c.parent_comment_id == this.local_comment.comment_id)
		},

		can_edit() {
			if (this.replies.length || this.resolved) return false

			return this.local_comment.author_id == this.user_info.user_id
		},

		can_toggle_resolved() {
			return this.local_comment.author_id == this.user_info.user_id
		},

		resolved() {
			return this.parent_resolved || this.local_comment.resolved
		},
	},

	mounted() {
		this.local_comment = new this.$Asset_Review_Comment(this.comment)
	},

	methods: {
		generate_new_comment() {
			this.new_comment = new this.$Asset_Review_Comment({
				comment_id: 'new',
				parent_comment_id: this.local_comment.comment_id,
				author_email: this.user_info.email,
				author_name: `${this.user_info.first_name} ${this.user_info.last_name}`,
				author_id: this.user_info.user_id,
			})
		},

		edit_comment() {
			this.editing = true
		},

		delete_comment(comment) {
			if (comment) {
				this.$emit('delete_comment', comment)
			} else {
				this.$confirm({
					title: 'Are you sure?',
					text: 'Are you sure you want to delete this comment?',
					acceptText: 'Delete',
					acceptColor: 'red',
				}).then(() => {
					this.$emit('delete_comment', this.local_comment)
				}).catch(() => {})
			}
		},

		toggle_resolved() {
			this.$set(this.local_comment, 'resolved', !this.local_comment.resolved)
			this.collapsed = this.local_comment.resolved
			this.save_comment(this.local_comment)
		},

		async save_comment(comment, top_level = true) {
			if (top_level) {
				let c = new this.$Asset_Review_Comment(comment)
				if (this.editing) {
					this.local_comment = c
				}
				this.$emit('save_comment', c)
			} else {
				this.$emit('save_comment', comment)
			}

			this.editing = false
			this.new_comment = null
		},

		copy_email_to_clipboard() {
			navigator.clipboard.writeText(this.local_comment.author_email)
			.then(() => {
					this.$inform('Email address copied to clipboard')
				}
			)
		},
	}
}
</script>

<style lang="scss">
.k-asset-review-comment, .k-asset-review-comment-editor {
	font-size: 14px;
	line-height: 18px;
	border-radius: 8px;
	width: 100%;
}

.k-asset-review-comment {
	background-color: $v-amber-lighten-5;
	border: 1px solid $v-amber;

	&.k-resolved {
		background-color: $v-green-lighten-5;
		border: 1px solid $v-green;
	}
}
.k-asset-review-comment-reply {
	background-color: #fff;
	margin-left: 32px;
    width: calc(100% - 40px);
}

.k-asset-review-comment-editor {
	background-color: #f8f8f8;
	border: 1px solid $v-amber;
}

.k-asset-review-comment-header {
	background-color: $v-amber-lighten-4;
	border-bottom: 1px solid $v-amber;
	padding: 8px;
	border-radius: 8px 8px 0 0;

	&.k-resolved {
		background-color: $v-green-lighten-4;
		border-bottom: 1px solid $v-green;
	}
}

.k-asset-review-comment-body {
	padding: 8px;

	&.k-collapsed {
		display: none;
	}
}
</style>
